<template>
    <div class="checkin-by-name">
        <!-- Locked out after 5 failed attempts -->
        <div v-if="isByNameLockedOut">
            <div class="alert alert-danger mt-3">
                {{ $t('checkin_by_name.error_locked_out') }}
            </div>
        </div>

        <!-- Step 1: Search by guest name -->
        <div v-else-if="action === 'search_name'">
            <p>{{ $t('checkin_by_name.instruction') }}</p>
            <form @submit.prevent="submitByName">
                <div class="form-group">
                    <label class="name-info">
                        {{ $t('checkin_by_name.label_checkin_date') }}
                    </label>
                    <input class="form-control text-center" type="text" :value="checkin_date" readonly />
                </div>
                <div class="form-group">
                    <label class="name-info">
                        {{ $t('checkin_by_name.label_guest_name') }}
                    </label>
                    <input 
                        class="form-control search_field" 
                        type="text" 
                        v-model="guest_name"
                        :placeholder="$t('checkin_by_name.name_placeholder')"
                        required 
                    />
                </div>
                <div class="text-right">
                    <button type="submit" class="btn btn-form-control btn-custom-primary" :disabled="!guest_name.trim()">
                        {{ $t("visit-information-form.confirm") }}
                    </button>
                </div>
                <div v-if="info_msg" class="alert alert-info mt-3 mb-0">
                    {{ info_msg }}
                </div>
                <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">
                    {{ error_msg }}
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import { booking_status } from '../../../utils/mockup';
import checkinByNameMixin, { BY_NAME_RESULT, BY_NAME_FEEDBACK } from '../../../utils/checkinByNameMixin';
import _ from "lodash";

export default {
    name: 'CheckinByName',
    mixins: [checkinByNameMixin],
    props: {
        facility: Object,
        confirm: Boolean,
        loadCustomTextSetting: Function,
        localized_filters: Array,
        selectFilter: Function,
        selected_localized_category: Object,
        loadQuestions: Function,
        booking_details: {
            type: Object,
            required: true
        },
        action: {
            type: String,
            required: true
        },
        cameFromPciHub: {
            type: Boolean,
            default: false
        },
        // Reservation check-in date to search by (facility timezone); the previous day during the window time extension
        checkinDate: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            info_msg: '',
            error_msg: '',
            guest_name: '',
        }
    },
    computed: {
        checkin_date() {
            return this.checkinDate || moment().format('YYYY/MM/DD');
        }
    },
    methods: {
        async submitByName() {
            this.resetMessages();

            if (this.isByNameLockedOut || !this.guest_name.trim()) return;

            const { result, booking } = await this.searchBookingByName({
                facility_id: this.facility.id,
                checkin_date: this.checkin_date,
                // Guest already completed pre-checkin (by phone or by name) - don't let the
                // PCI hub flow re-enter the confirm/finish-precheckin screen and re-trigger it.
                preCheckinIsCompleted: this.cameFromPciHub,
            });

            // Reset questionnaire form to input
            this.confirm = false;

            if (result !== BY_NAME_RESULT.found) {
                this.showByNameFeedback(result);
                return;
            }

            // Case 1: Success — single match found
            const status = booking.booking_status_id;
            const matchBooking = _.cloneDeep({ data: [booking], total: 1 });

            this.$emit('update-booking', matchBooking);

            if (status === booking_status.pre_check_in) {
                this.$emit('update-next', 'checkin-form');
            } else {
                if (this.localized_filters.length > 0) {
                    this.$emit('update-next', 'localized-filters');
                    this.$emit('update-action', '');
                } else {
                    this.$emit('update-next', 'checkin-form');
                    this.$emit('update-action', '');
                }
            }
        },

        showByNameFeedback(result) {
            // Locked out — the template swaps to the locked out message
            if (result === BY_NAME_RESULT.locked_out) return;

            const { type, key } = BY_NAME_FEEDBACK[result];
            if (type === 'info') {
                this.info_msg = this.$t(key);
                return;
            }
            this.errorDisplay(this.$t(key));
        },

        resetMessages() {
            this.info_msg = '';
            this.error_msg = '';
        },

        resetState() {
            this.resetMessages();
            this.resetByNameState();
        },

        errorDisplay(errorMsg) {
            this.error_msg = errorMsg;
            setTimeout(() => {
                this.error_msg = '';
            }, 4000);
        },
    }
}
</script>

<style scoped>

.checkin-by-name p {
    font-size: 1rem !important;
    padding-top: 24px !important;
    margin-bottom: 12px;
}

.btn-form-control {
    min-width: 156px;
    border-radius: 20px;
}

.btn-custom-primary {
    color: white;
    border: 1px solid #1aa8de !important;
    background-color: #1aa8de !important;

    &:not([disabled]):hover {
        border: 1px solid #0b89bb !important;
        background-color: #0b89bb !important;
    }
}

.search_field {
    text-align: center;
}
</style>